import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Edit, Layers } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import { useGetProductsQuery, useGetProductGroupsQuery } from "../productsApi";
import { useCanCreateProducts } from "../useProductPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import ProductBarcodeButton from "./ProductBarcodeButton";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// "Basic View" pane of the master-detail split, third module on this pattern
// after Employee Master and Customers (2026-08-29) - built on the same
// reusable MasterDetailListShell/MasterDetailRow.
const ProductCompactList = () => {
  const canCreate = useCanCreateProducts();
  const navigate = useNavigate();
  const { recordGuid: selectedRecordGuid } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [groupKey, setGroupKey] = useState(""); // `${branchId}-${groupId}`
  const { selectedBranchId, availableBranches } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: products = [], isLoading } = useGetProductsQuery({
    search: search || undefined,
    isActive: status === "" ? undefined : status === "active",
    branchId: selectedBranchId ?? undefined,
  });

  const { data: allGroups = [] } = useGetProductGroupsQuery();
  const groups = selectedBranchId
    ? allGroups.filter((g) => g.branchId === selectedBranchId)
    : allGroups;

  useEffect(() => {
    if (groupKey && !groups.some((g) => `${g.branchId}-${g.groupId}` === groupKey)) {
      setGroupKey("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId]);

  // Type-to-search group filter (2026-09-23), keyed {branchId}-{groupId} as before.
  const groupOptions = [...groups]
    .sort((a, b) => (a.groupName || "").localeCompare(b.groupName || ""))
    .map((g) => ({
      value: `${g.branchId}-${g.groupId}`,
      label: `${g.groupName || `Group ${g.groupId}`}${!selectedBranchId ? ` (${availableBranches.find((b) => b.branchId === g.branchId)?.branchCode || g.branchId})` : ""}`,
    }));

  const filtered = groupKey
    ? products.filter((p) => `${p.branchId}-${p.productGroupId}` === groupKey)
    : products;

  const initials = (p) => (p.productName || "?").trim().slice(0, 2).toUpperCase();

  const scopeBranchCode = selectedBranchId
    ? availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode
    : null;
  // Grouped-by-Product-Group export (groupBy sorts the groups themselves;
  // pre-sorting by name here means each group's own products come out
  // alphabetical too, since grouping only partitions, it doesn't re-sort).
  // Carried over from the old flat ProductsList.jsx, which this split view
  // replaced without bringing this along.
  const exportRows = [...filtered].sort((a, b) => (a.productName || "").localeCompare(b.productName || ""));

  return (
    <MasterDetailListShell
      title="Products"
      addButton={
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-primary" onClick={() => navigate(paths.productGroups)}>
            <Layers className="me-2" size={16} />
            Groups
          </button>
          {canCreate && (
            <button type="button" className="btn btn-added" onClick={() => navigate(paths.productNew)}>
              <PlusCircle className="me-2" size={16} />
              Add New Product
            </button>
          )}
        </div>
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search product name or code"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <Select
              classNamePrefix="react-select"
              isClearable
              isSearchable
              options={groupOptions}
              value={groupOptions.find((o) => o.value === groupKey) || null}
              onChange={(opt) => setGroupKey(opt ? opt.value : "")}
              placeholder="All Groups - type to search"
              noOptionsMessage={() => "No matching groups"}
              styles={{ container: (base) => ({ ...base, flex: 1, minWidth: 180, fontSize: 13 }) }}
            />
            <select className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ListExportToolbar
              title="Products"
              subtitleLines={[scopeBranchCode ? `Branch: ${scopeBranchCode}` : "All Branches"]}
              branchCode={scopeBranchCode}
              columns={[
                { header: "Code", value: (r) => r.productCodeNew },
                { header: "Name", value: (r) => r.productName },
                { header: "Branch", value: (r) => r.branchCode },
                { header: "Sales Rate", value: (r) => r.salesRate, align: "right" },
                { header: "Status", value: (r) => (r.isActive ? "Active" : "Inactive") },
              ]}
              rows={exportRows}
              groupBy={(r) => r.productGroupName || "Ungrouped"}
              fileName="Products"
            />
          </div>
        </>
      }
      isLoading={isLoading}
      emptyLabel="No products match these filters."
      items={filtered}
      getKey={(p) => p.recordGuid}
      resetKey={`${search}|${status}|${groupKey}|${selectedBranchId}`}
      renderItem={(p) => {
        const isSelected = String(p.recordGuid) === String(selectedRecordGuid);
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.productEdit(p.recordGuid))}
            avatarLabel={initials(p)}
            title={
              <>
                {p.productName || "-"}
                <span className="text-muted ms-1">#{p.productCodeNew}</span>
                {p.isMapped && (
                  <span className="badge bg-info-transparent ms-2" title={`Home branch: ${p.branchCode}`}>
                    Shared from {p.branchCode}
                  </span>
                )}
              </>
            }
            subtitle={<>{p.productGroupName || "Ungrouped"} &middot; {p.branchCode}</>}
            badge={
              <span className={`badge ${p.isActive ? "badge-linesuccess" : "badge-linedanger"} me-2`}>
                {p.isActive ? "Active" : "Inactive"}
              </span>
            }
            actions={
              <>
                <ProductBarcodeButton branchId={p.branchId} productCode={p.productCode} productName={p.productName} />
                <button
                  type="button"
                  className="btn btn-link p-1"
                  title="Edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(paths.productEdit(p.recordGuid));
                  }}
                >
                  <Edit size={15} />
                </button>
              </>
            }
          />
        );
      }}
    />
  );
};

export default ProductCompactList;
